import { Link } from 'react-router-dom';
import { Button } from '../components/ui/button';
import { SEOHead } from '../components/SEOHead';
import { 
  Plane, Clock, Shield, Star, Phone, MapPin, 
  CheckCircle, ArrowRight, Users, Car, Briefcase, ShoppingBag
} from 'lucide-react';

const FLEET_IMG = 'https://customer-assets.emergentagent.com/job_f40a108f-f3a3-4735-ac15-18353f3eece6/artifacts/sih2jxml_image0.jpeg';
const ESCALADE_IMG = 'https://customer-assets.emergentagent.com/job_31dc5cc9-4948-412e-afd9-88900b02bc09/artifacts/g0b5gai9_IMG_7208.jpg';
const TYSONS_IMG = 'https://images.pexels.com/photos/14700127/pexels-photo-14700127.jpeg?auto=compress&cs=tinysrgb&dpr=2&h=650&w=940';

const schema = {
  "@context": "https://schema.org",
  "@type": "LocalBusiness",
  "name": "Capital Chauffeur Services - Tysons Corner",
  "telephone": "+1-202-684-0534",
  "email": "contact@capitalchauffeurservices.com",
  "address": {
    "@type": "PostalAddress",
    "addressLocality": "Tysons",
    "addressRegion": "VA",
    "addressCountry": "US"
  },
  "geo": {
    "@type": "GeoCoordinates",
    "latitude": "38.9187",
    "longitude": "-77.2311"
  },
  "priceRange": "$$$",
  "image": FLEET_IMG,
  "description": "Premium black car service in Tysons Corner, Virginia. Executive transportation, airport transfers, corporate chauffeur. Luxury Escalade fleet.",
  "areaServed": ["Tysons Corner", "Tysons", "McLean", "Vienna", "Falls Church"],
  "hasOfferCatalog": {
    "@type": "OfferCatalog",
    "name": "Black Car Services",
    "itemListElement": [
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Airport Car Service" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Corporate Chauffeur" } },
      { "@type": "Offer", "itemOffered": { "@type": "Service", "name": "Executive Transportation" } }
    ]
  }
};

export default function TysonsPage() {
  return (
    <div data-testid="tysons-page" className="min-h-screen bg-[#050505]">
      <SEOHead
        title="Black Car Service Tysons Corner VA | Executive Chauffeur | Capital Chauffeur Services"
        description="Premium black car service in Tysons Corner, Virginia. Executive transportation, airport transfers, corporate chauffeur. Luxury Escalade fleet. Book now."
        canonical="/black-car-service-tysons"
        schema={schema}
      />

      {/* Hero */}
      <section className="relative min-h-[70vh] flex items-center">
        <div className="absolute inset-0">
          <img
            src={ESCALADE_IMG}
            alt="Black car service Tysons Corner Virginia luxury Cadillac Escalade"
            className="w-full h-full object-cover"
          />
          <div className="absolute inset-0 bg-gradient-to-r from-black/90 via-black/70 to-black/40" />
        </div>
        <div className="relative z-10 max-w-7xl mx-auto px-6 py-32">
          <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-4 font-medium">Tysons Corner, Virginia</p>
          <h1 data-testid="tysons-h1" className="text-4xl sm:text-5xl lg:text-6xl font-serif text-white mb-6 leading-tight">
            Black Car Service<br />in Tysons Corner, Virginia
          </h1>
          <p className="text-[#A3A3A3] text-lg max-w-xl mb-8 leading-relaxed">
            Executive black car service in Northern Virginia's premier business and retail destination. Professional chauffeurs serving Tysons Corner, McLean, and the surrounding Dulles Corridor.
          </p>
          <div className="flex flex-wrap gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase">
                Book Your Ride
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-8 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>

      {/* About Tysons */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto grid lg:grid-cols-2 gap-16 items-center">
          <div>
            <p className="text-[#D4AF37] text-sm tracking-[0.3em] uppercase mb-3">About Tysons Corner</p>
            <h2 className="text-3xl font-serif text-white mb-6">Tysons Corner: Northern Virginia's Business Capital</h2>
            <div className="space-y-4 text-[#A3A3A3] leading-relaxed">
              <p>
                Tysons Corner has transformed from a suburban crossroads into one of the East Coast's most important business districts. With the arrival of the Silver Line Metro, billions in new development, and a concentration of Fortune 500 companies, Tysons is now the economic engine of Northern Virginia and the largest commercial district in the DC metro area outside of downtown Washington itself.
              </p>
              <p>
                The area is headquarters to Capital One Financial, Booz Allen Hamilton, Freddie Mac, Gannett, and dozens of other major corporations. Tysons Corner Center and Tysons Galleria draw high-end shoppers from across the region. The Ritz-Carlton Tysons Corner, Hilton McLean, and Archer Hotel cater to business travelers and luxury guests.
              </p>
              <p>
                Capital Chauffeur Services provides premium black car transportation throughout Tysons Corner and the surrounding communities of McLean, Vienna, and Falls Church. Our professional chauffeurs are familiar with Tysons' evolving road network, its mixed-use developments, and the specific needs of the corporate executives, consultants, and visiting professionals who work here.
              </p>
            </div>
          </div>
          <div className="aspect-[4/3] overflow-hidden">
            <img
              src={TYSONS_IMG}
              alt="Modern glass office building in Tysons Corner Virginia business district"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Services */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="text-center mb-16">
            <h2 className="text-3xl font-serif text-white mb-4">Tysons Corner Transportation Services</h2>
          </div>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-8">
            {[
              { icon: Briefcase, title: 'Corporate Headquarters Shuttles', desc: 'Dedicated transportation for executives at Capital One, Booz Allen, Freddie Mac, and other Tysons-based corporations. Account billing available.' },
              { icon: Plane, title: 'Dulles Airport Transfers', desc: 'Tysons to Dulles International Airport in just 15-25 minutes. Flight tracking, curbside pickup, and meet-and-greet service available.' },
              { icon: Clock, title: 'Hourly Executive Service', desc: 'Multi-stop chauffeur service for Tysons business days. Office visits, client meetings, lunch appointments — one driver, one vehicle, your schedule.' },
              { icon: ShoppingBag, title: 'Tysons Galleria & Shopping', desc: 'Luxury car service for upscale shopping excursions at Tysons Galleria and Tysons Corner Center. Door-to-door with luggage assistance.' },
              { icon: Star, title: 'Hotel & Event Transportation', desc: 'Premium transfers to and from Ritz-Carlton Tysons, Hilton McLean, and Hyatt Regency. Event transportation for corporate galas and conferences.' },
              { icon: Users, title: 'Visiting Client Transport', desc: 'Impress visiting clients with professional black car service. Airport pickup, hotel transfer, and chauffeured transportation to your Tysons office.' },
            ].map((service, i) => (
              <div key={i} className="border border-white/10 p-8 hover:border-[#D4AF37]/30 transition-colors">
                <service.icon className="w-8 h-8 text-[#D4AF37] mb-4" />
                <h3 className="text-white font-serif text-lg mb-3">{service.title}</h3>
                <p className="text-[#A3A3A3] text-sm leading-relaxed">{service.desc}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Routes */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-8 text-center">Popular Routes from Tysons Corner</h2>
          <div className="grid md:grid-cols-2 lg:grid-cols-3 gap-6">
            {[
              { route: 'Tysons to Dulles Airport (IAD)', time: '15-25 min' },
              { route: 'Tysons to Reagan Airport (DCA)', time: '25-40 min' },
              { route: 'Tysons to Downtown DC', time: '30-50 min' },
              { route: 'Tysons to Reston', time: '10-15 min' },
              { route: 'Tysons to Arlington', time: '15-25 min' },
              { route: 'Tysons to Bethesda, MD', time: '25-35 min' },
            ].map((item, i) => (
              <div key={i} className="flex items-center justify-between border border-white/10 p-5">
                <div className="flex items-center gap-3">
                  <MapPin className="w-4 h-4 text-[#D4AF37]" />
                  <span className="text-white text-sm">{item.route}</span>
                </div>
                <span className="text-[#D4AF37] text-xs">{item.time}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Fleet */}
      <section className="py-16 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <div className="aspect-[21/9] overflow-hidden">
            <img
              src={FLEET_IMG}
              alt="Capital Chauffeur Services black Escalade fleet for Tysons Corner executive car service"
              className="w-full h-full object-cover"
            />
          </div>
        </div>
      </section>

      {/* Areas Served */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-4 text-center">Tysons Corner Area Coverage</h2>
          <p className="text-[#A3A3A3] text-center mb-10 max-w-2xl mx-auto">
            We serve Tysons Corner and all adjacent communities, providing seamless connections to Washington, DC and the wider region.
          </p>
          <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
            {[
              'Tysons Corner', 'McLean', 'Vienna', 'Falls Church',
              'Merrifield', 'Dunn Loring', 'Pimmit Hills', 'Wolf Trap',
            ].map((area, i) => (
              <div key={i} className="flex items-center gap-3 border border-white/10 p-4">
                <MapPin className="w-4 h-4 text-[#D4AF37] flex-shrink-0" />
                <span className="text-white text-sm">{area}</span>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Why Choose */}
      <section className="py-20 px-6 bg-[#0A0A0A]">
        <div className="max-w-7xl mx-auto">
          <h2 className="text-3xl font-serif text-white mb-10 text-center">Why Tysons Corner Professionals Choose Us</h2>
          <div className="grid md:grid-cols-2 gap-8">
            {[
              'Strategically positioned near Tysons for the fastest response times to any business district address',
              'Just 15 minutes to Dulles Airport — the quickest and most reliable IAD transfer service from Tysons',
              'Trusted by Fortune 500 companies headquartered in Tysons for executive and client transportation',
              'Premium black Cadillac Escalade ESV fleet with executive amenities, Wi-Fi ready, and privacy glass',
              'Experienced chauffeurs who navigate Tysons\' evolving road network and construction zones daily',
              'Corporate accounts with monthly billing, trip reporting, and dedicated account management',
            ].map((item, i) => (
              <div key={i} className="flex items-start gap-4 p-4">
                <CheckCircle className="w-5 h-5 text-[#D4AF37] mt-0.5 flex-shrink-0" />
                <p className="text-[#A3A3A3] leading-relaxed">{item}</p>
              </div>
            ))}
          </div>
        </div>
      </section>

      {/* Internal Links */}
      <section className="py-20 px-6">
        <div className="max-w-7xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Explore Our Other Services</h2>
          <div className="flex flex-wrap justify-center gap-4 mt-10">
            {[
              { label: 'Dulles Airport Car Service', path: '/dulles-airport-car-service' },
              { label: 'Reagan Airport Car Service', path: '/reagan-airport-car-service' },
              { label: 'Corporate Chauffeur NoVA', path: '/corporate-chauffeur-northern-virginia' },
              { label: 'Black Car Service Arlington', path: '/black-car-service-arlington' },
              { label: 'All Services', path: '/services' },
            ].map((link, i) => (
              <Link key={i} to={link.path}>
                <Button variant="outline" className="border-white/20 text-[#A3A3A3] hover:border-[#D4AF37] hover:text-[#D4AF37] rounded-none px-6 py-3 text-xs tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                  {link.label} <ArrowRight className="w-3 h-3" />
                </Button>
              </Link>
            ))}
          </div>
        </div>
      </section>

      {/* CTA */}
      <section className="py-20 px-6 bg-[#0A0A0A] border-t border-white/5">
        <div className="max-w-3xl mx-auto text-center">
          <h2 className="text-3xl font-serif text-white mb-4">Book Your Tysons Corner Black Car Service</h2>
          <p className="text-[#A3A3A3] mb-8">
            Premium executive transportation in Tysons Corner, Virginia. Professional chauffeurs, luxury vehicles, impeccable service.
          </p>
          <div className="flex flex-wrap justify-center gap-4">
            <Link to="/book">
              <Button className="bg-[#D4AF37] text-black hover:bg-[#B5952F] rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase">
                Book Now
              </Button>
            </Link>
            <a href="tel:+12026840534">
              <Button className="border border-[#D4AF37] text-[#D4AF37] hover:bg-[#D4AF37] hover:text-black rounded-none px-10 py-6 text-sm font-medium tracking-wider uppercase bg-transparent inline-flex items-center gap-2">
                <Phone className="w-4 h-4" /> Call (202) 684-0534
              </Button>
            </a>
          </div>
        </div>
      </section>
    </div>
  );
}
